import { UseQueryResult, useQuery } from 'react-query'
import useApi from 'hooks/useApi'
import { getLeadRoute } from 'dataSource/salesforce/routes'
import { useEffect, useState } from 'react'
import { transformedLeadData } from './utils'

const useFetchSalesforceLead = (
  storedLeadId: string,
  shouldFetchLeadDetails: boolean
): UseQueryResult<SalesForceLeadResults> => {
  const { request: leadRequest } = useApi({
    redirectOnUnauthorized: false,
  })
  const [prevData, setPrevData] = useState(null)

  // Lead Salesforce IDs always start with "00Q". Guard against legacy
  // call sites that pass a Case Id (prefix "500") via a leadId param.
  const isValidLeadId =
    Boolean(storedLeadId) &&
    storedLeadId !== 'undefined' &&
    storedLeadId.startsWith('00Q')
  const shouldFetch = shouldFetchLeadDetails && isValidLeadId

  const leadQueryData = useQuery<SfLead, Error, SalesForceLeadResults>(
    ['lead', storedLeadId],
    () => getLeadData(leadRequest, storedLeadId),
    {
      enabled: shouldFetch,
      refetchOnWindowFocus: 'always',
      select(data) {
        return transformedLeadData(data, prevData)
      },
      isDataEqual(oldData, newData) {
        return oldData?.LastModifiedDate === newData?.LastModifiedDate
      },
    }
  )

  useEffect(() => {
    setPrevData(leadQueryData?.data?.lead)
  }, [leadQueryData?.data?.lead])

  return leadQueryData
}

const getLeadData = async (leadRequest, id) => {
  if (!id || id === 'undefined' || id === 'null') {
    throw new Error('Lead ID is required')
  }

  const response = await leadRequest.get(getLeadRoute(id))

  if (!response) {
    throw new Error('Unable to fetch lead details')
  }

  if (response?.statusCode === 401) {
    throw new Error('Unauthorized')
  }

  return response
}

export default useFetchSalesforceLead
